/**
 * Pipeline-strip component — a horizontal 9-stage timeline showing
 * a bundle's progress through the triage worker's intake → analyze
 * → complete pipeline.
 *
 * Landing target for the Slack "analyzing" message link and the
 * primary hero element of the `/bundles/:uuid` page (Wave 11 per
 * NEXT_STEPS Appendix D.6). Each stage corresponds to a
 * `bundle_lifecycle.*_at` timestamp column:
 *
 *   arrived → retrieved_from_s3 → triage_completed → classified →
 *   dedup_decided → analysis_started → analysis_finished → completed
 *
 * # Stage status model
 *
 * Each stage is one of:
 *
 * - `done` — the corresponding `_at` timestamp is non-NULL. Pill
 *   renders with a green background, a ✓ glyph, and the elapsed
 *   time since the PREVIOUS stage (e.g. "+2.3s"). The label under
 *   the FIRST done stage shows the absolute time.
 * - `in_progress` — the previous stage is done but this one is
 *   not; the bundle is currently here. Pill renders amber with a
 *   spinner and a live-ticking elapsed counter ("3m 42s") driven
 *   by a component-local 1 Hz `setInterval`.
 * - `pending` — neither this stage nor any prior stage is in
 *   progress; waiting for a predecessor to complete. Pill renders
 *   gray with a ○ glyph and no time label.
 * - `failed` — the bundle hit an error at or before this stage.
 *   Rendered as a red pill with a ✕ glyph. No live timer.
 * - `skipped` — the bundle was deduped / version-gated / platform-
 *   skipped at an earlier stage so this stage will never run.
 *   Rendered as a gray pill with a dash glyph and an italic label.
 *
 * # Live timer semantics
 *
 * The `useLiveElapsed` hook inside this file returns an integer
 * seconds-since-`startedAt` value that re-renders every 1000 ms —
 * but ONLY when at least one stage has status `in_progress`. A
 * pipeline that's fully done + completed OR fully failed doesn't
 * tick, so a page with 500 stale pipeline strips (unlikely) won't
 * burn CPU on timers.
 *
 * # Compact vs. full rendering
 *
 * `PIPELINE_STAGES` has 9 entries (arrived → retrieved → triaged →
 * classified → dedup_decided → analysis_started → analysis_finished
 * → completed → patches_notify, the last added by TRIAG-270 for the
 * 30-60 min patch-generation + Slack-post tail). At realistic
 * sub-label widths ("11:36:15 AM",
 * "user-filed +10s", "queued 22m 54s") each pill renders at roughly
 * 115–135 px, so the full single-row layout needs ~1100 px of
 * horizontal space inside its parent container before the chevrons
 * and gaps push it over the right edge.
 *
 * 2026-05-11 fix: the breakpoint for `flex-nowrap` is `xl` (1280 px
 * viewport) — wide enough that the strip's content actually fits the
 * page's bordered section at typical desktop widths. Below `xl` the
 * pills wrap to multi-row. As a belt-and-suspenders against any
 * residual overflow at exotic widths (narrow desktop, half-screen
 * window, custom dashboards inside split panes) the `<ol>` is wrapped
 * in an `overflow-x-auto` container so a too-wide strip becomes
 * horizontally scrollable rather than silently clipping past the
 * section's rounded-border right edge. The prior `md:flex-nowrap`
 * (768 px) shape claimed to fit 8 pills in any md+ viewport but the
 * math never worked — visible bug on the first gated bundle in
 * staging on 2026-05-11.
 *
 * Galley #11793: with a 9th stage ("Patches + notify") the `xl`
 * one-row form stopped fitting too (1,116 px of pills in a 942 px
 * column at every desktop width), so the strip now wraps at every
 * width and the chevrons are gone.
 */

import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { formatDateTime, zoneAbbrev } from "@/lib/timezone";

// ─── Stage definitions ──────────────────────────────────────────────────────

/**
 * Canonical ordered list of pipeline stages. The order here is the
 * order they appear left-to-right on screen AND the order that
 * determines which stage is `in_progress` (the first stage whose
 * predecessor is done but which is itself not done).
 *
 * Stages correspond to columns on `bundle_lifecycle`
 * (`arrived_at`, `retrieved_from_s3_at`, etc.). When the backend
 * adds a new lifecycle timestamp (unlikely but possible), this
 * list gains an entry and the `StageName` union below picks it up
 * via `typeof`.
 */
// TRIAG-46: canonical pipeline-stage tuple colocated with the strip
// component; many callers consume it. HMR degradation acceptable per
// eslint.config.js posture.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export const PIPELINE_STAGES = [
  "arrived",
  "retrieved",
  "triaged",
  "classified",
  "dedup_decided",
  "analysis_started",
  "analysis_finished",
  "completed",
  // TRIAG-270 — the patches + Slack-notify tail. `completed` is NOT
  // terminal for analyzed bundles: Step 8 patch generation (30-60 min)
  // runs between `completed_at` and the deferred Slack post. The pill
  // is timestamped from `bundle_lifecycle.slack_notify_posted_at`
  // (full row, not the status timestamps) — see
  // `pipelineStages.ts::deriveStages`.
  "patches_notify",
] as const;

export type StageName = (typeof PIPELINE_STAGES)[number];

/**
 * Human-readable label for a stage. Short enough to fit in a pill
 * at a reasonable font size — 1–2 words. "dedup_decided" collapses
 * to "Dedup" which is slightly lossy but operator-friendly (the
 * methodology breakdown is shown as a secondary label in a tooltip
 * anyway).
 */
// TRIAG-46: human-readable label helper colocated with the strip
// component; HMR degradation acceptable.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export function stageLabel(s: StageName): string {
  switch (s) {
    case "arrived":
      return "Arrived";
    case "retrieved":
      return "Retrieved";
    case "triaged":
      return "Triaged";
    case "classified":
      return "Classified";
    case "dedup_decided":
      return "Dedup";
    case "analysis_started":
      return "Analyzing";
    case "analysis_finished":
      return "Analyzed";
    case "completed":
      return "Completed";
    // TRIAG-270 — short for "patch generation + Slack notify"; the
    // tooltip + sub-label carry the detail.
    case "patches_notify":
      return "Patches + notify";
  }
}

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * The status of a single stage. See the component module docs for
 * the semantic of each variant.
 */
export type StageStatus =
  | "done"
  | "in_progress"
  | "queued"
  // Migration 026 (2026-05-12) — `gated` is rendered on the
  // head-of-pipeline slot when `intake_policy === "manual"` and
  // `analysis_started_at === null`. Replaces the misleading
  // `queued` rendering on awaiting-action bundles.
  | "gated"
  // Migration 026 (2026-05-12) — `dismissed` is rendered on the
  // head-of-pipeline slot when `dismissed_at !== null`.
  // Terminal state: the pipeline will not run for this bundle
  // unless an operator clicks `[↺ Undismiss]`.
  | "dismissed"
  | "pending"
  | "failed"
  | "skipped";

/**
 * One stage's data — the status + the timestamp (if it's done) +
 * an optional secondary label (rendered below the stage name, for
 * e.g. the dedup methodology on the `dedup_decided` stage).
 */
export interface StageData {
  name: StageName;
  status: StageStatus;
  /** ISO 8601 — when the stage completed. Required when `status === "done"`. */
  ts?: string | null;
  /**
   * Optional secondary label. Rendered italicized beneath the
   * stage name. Used for e.g. `dedup_decided` to show
   * "exact hash" vs "vector+LLM" vs "first occurrence".
   */
  subLabel?: string | null;
}

export interface PipelineStripProps {
  /**
   * The 8 stages (or fewer, if a page wants to hide some). The
   * component does NOT enforce that stages appear in
   * `PIPELINE_STAGES` order — the caller passes them in the order
   * they want them rendered, which is almost always canonical order.
   */
  stages: StageData[];

  /**
   * The timestamp the pipeline first became relevant — typically
   * `bundle_lifecycle.arrived_at`. Used as the base for the
   * `in_progress` live timer when the in-progress stage has no
   * predecessor to time from. Optional — defaults to the first
   * done stage's `ts`, or `now()` if no stage is done yet.
   */
  pipelineStartedAt?: string | null;

  /**
   * Per-phase median durations from
   * `GET /api/analysis-phases/medians`. When present AND the
   * pipeline is currently in-progress AND the in-progress stage
   * is `analysis_started` (the only stage whose sub-phases the
   * medians describe), the strip renders an
   * `Expected remaining: ~Nm` chip below the in-progress pill.
   * When absent / empty / the in-progress stage isn't
   * `analysis_started`, the chip is suppressed.
   *
   * Landed 2026-05-01 as P2b Item 5 — the backend endpoint has
   * been live since `6f86629` (2026-04-30) but this is the first
   * consumer. See `computeExpectedRemainingMs` below for the
   * "sum the medians of phases not yet seen" algorithm + the
   * sample-thickness guard.
   */
  phaseMedians?: PhaseMedianLike[] | null;

  /**
   * Phase labels already observed on THIS analysis (from the
   * `analysis_phases` rows the BundleDetail page has already
   * fetched). Used by `computeExpectedRemainingMs` to figure out
   * which phase-label medians to ADD into the remaining estimate
   * and which to skip (already completed). When omitted the ETA
   * is suppressed — without this the remaining estimate would be
   * "sum of ALL medians" which is the full-analysis estimate, not
   * the remaining-time estimate, and that's actively misleading.
   */
  completedPhaseLabels?: string[];

  /**
   * 2026-05-01 Bug 10 fix: when `false`, the ETA chip is
   * suppressed AND the `analysis_started` pill renders as
   * `"queued"` (static, purple, no live-ticking timer) instead
   * of `"in_progress"` (amber, pulsing, live timer). Derived by
   * the consuming page from
   * `bundle_lifecycle.analysis_started_at IS NOT NULL
   *    AND completed_at IS NULL AND last_error IS NULL`.
   *
   * Defaults to `true` so existing callers (tests, future
   * pages) behave as before — the queued-state treatment is
   * opt-in on the `BundleDetail` page today.
   */
  isActivelyAnalyzing?: boolean;

  /**
   * Optional className merged onto the root wrapper. Lets a page
   * tune margin / width without the component growing a layout
   * prop for every combination.
   */
  className?: string;
}

/**
 * Minimal shape `PipelineStrip` needs from a `PhaseMedian`. Kept
 * as a structural subset of the full wire type (`PhaseMedian` in
 * `@/lib/api`) so `PipelineStrip` stays agnostic of how its
 * consumer fetched the data — tests pass literals, the live page
 * passes `observabilityQueries.phaseMedians().data.rows`.
 */
export interface PhaseMedianLike {
  phase_label: string;
  median_duration_ms: number;
  sample_count: number;
}

// ─── Live elapsed hook ──────────────────────────────────────────────────────

/**
 * Return the whole-seconds elapsed since `sinceIso`, re-rendering
 * every 1000 ms as long as `active` is true. When `active` flips
 * to false the timer is torn down and the component stops re-
 * rendering.
 *
 * Returns `null` if `sinceIso` is invalid (unparseable) or
 * `active` is false on mount — callers should render a non-live
 * label in that case.
 */
function useLiveElapsed(
  sinceIso: string | null | undefined,
  active: boolean,
): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    // One-Hz tick while an in-progress stage exists. Cleared on
    // unmount / on `active` flipping false. `setInterval` is
    // preferred over `requestAnimationFrame` — the user doesn't
    // care about sub-second precision on an elapsed counter,
    // and rAF would keep the render loop busy for no benefit.
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!sinceIso) return null;
  const ts = Date.parse(sinceIso);
  if (Number.isNaN(ts)) return null;
  return Math.max(0, Math.floor((now - ts) / 1000));
}

/**
 * Format a seconds count as a compact duration — the same shape
 * used by the static `+Ns` labels below so the live counter
 * visually matches after it finishes.
 *
 *   0–59s  → "Ns" (e.g. "42s")
 *   1–59m  → "Nm Ms" (e.g. "3m 42s")
 *   ≥1h    → "Nh Mm" (e.g. "2h 15m")
 *
 * Negative values clamp to 0 — should never happen given the
 * `Math.max` in `useLiveElapsed`, but defensive.
 */
function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

/**
 * Format the delta between two ISO timestamps, e.g. "+2.3s" or
 * "+1m 14s". Used as the label under a done stage showing how
 * long after the previous stage it completed.
 */
function formatDelta(fromIso: string, toIso: string): string {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return "";
  const deltaMs = Math.max(0, to - from);
  if (deltaMs < 1000) return `+${deltaMs}ms`;
  if (deltaMs < 10_000) {
    // Sub-10-second deltas get one decimal for readability
    // ("+2.3s" conveys more than "+2s" at this resolution).
    return `+${(deltaMs / 1000).toFixed(1)}s`;
  }
  return `+${formatDuration(deltaMs / 1000)}`;
}

/**
 * Format an ISO timestamp as a short absolute clock time for the
 * first done stage's label. Uses the browser's locale, in the zone the
 * top bar's switch chose (Galley #21943: the viewer's own by default,
 * as before). The pill stays compact; its hover names the zone.
 */
function formatClock(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatDateTime(iso, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    label: false,
  });
}

// ─── Pill style tokens ──────────────────────────────────────────────────────
//
// Each status maps to a background + text + border color tuple.
// Kept as a const object so adding a new status is a one-line
// addition and the `switch` in the pill renderer stays exhaustive
// via TypeScript's `Record<StageStatus, …>`.

const STATUS_STYLES: Record<StageStatus, string> = {
  done: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  in_progress:
    "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
  // Migration 026 (2026-05-12) — the `gated` variant replaces the
  // misleading `queued` chip on a bundle whose `intake_policy ==
  // "manual"` and no analysis has started yet. Amber matches the
  // banner color (consistent visual vocabulary across the page).
  // Pre-026, gated bundles rendered as `queued` (purple) which
  // implied "waiting for a worker slot" — actually false; the
  // bundle is waiting for an operator decision.
  gated:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100",
  // Migration 026 (2026-05-12) — the `dismissed` variant renders
  // a terminal-state gray pill on the head-of-pipeline slot when
  // an operator dismissed the bundle without escalating it.
  // Strike-through emphasizes the "this pipeline will not run"
  // semantic; gray matches the `skipped` treatment used for
  // dedup-skipped analysis stages.
  dismissed:
    "border-gray-300 bg-gray-100 text-gray-600 line-through dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400",
  // 2026-05-01 Bug 7 fix: the `queued` variant renders in
  // purple to match the `/bundles` list's Route column `queued`
  // chip (`color: "purple"` in `Bundles.tsx::deriveRoute`). A
  // consistent visual vocabulary across the list AND the
  // detail page means an operator who sees ⏱ purple on the
  // list and then clicks through to the detail page sees the
  // same signal — rather than the pre-fix amber-pulsing
  // "Analyzing" pill which actively misleads that a worker
  // has picked the bundle up.
  queued:
    "border-purple-300 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-200",
  pending:
    "border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-500",
  failed:
    "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200",
  skipped:
    "border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500",
};

const STATUS_GLYPHS: Record<StageStatus, string> = {
  done: "✓",
  in_progress: "●",
  // ⏱ matches the `/bundles` Route column's queued glyph
  // exactly so the visual anchor carries across pages.
  queued: "⏱",
  // Migration 026 (2026-05-12) — `🚦` traffic-light matches
  // the `🚦 Auto-off` chip the `/bundles` row already shows
  // and the awaiting-action banner header glyph.
  gated: "🚦",
  // Migration 026 (2026-05-12) — `👋` mirrors the dismiss
  // button label on the AwaitingActionBanner. Same glyph
  // across button, modal title, and strip pill.
  dismissed: "👋",
  pending: "○",
  failed: "✕",
  skipped: "–",
};

/**
 * Human-readable status text for screen readers via `aria-label`.
 * Separate from `STATUS_GLYPHS` because the glyph is visual noise
 * for assistive tech ("circle circle circle checkmark") whereas
 * "done" / "in progress" conveys meaning.
 */
const STATUS_ARIA: Record<StageStatus, string> = {
  done: "done",
  in_progress: "in progress",
  // Screen readers hear "queued, waiting for a worker slot"
  // so the distinction from "in progress" is audible too, not
  // just visual. Matches the Bug 11 banner's phrasing so the
  // detail page reads coherently.
  queued: "queued, waiting for a worker slot",
  // Migration 026 (2026-05-12) — screen readers hear the same
  // text the banner carries so the audible signal matches the
  // visual one.
  gated: "auto-analysis off, awaiting operator action",
  dismissed: "dismissed by operator",
  pending: "pending",
  failed: "failed",
  skipped: "skipped",
};

// ─── Root component ─────────────────────────────────────────────────────────

export function PipelineStrip({
  stages,
  pipelineStartedAt,
  phaseMedians,
  completedPhaseLabels,
  isActivelyAnalyzing = true,
  className,
}: PipelineStripProps) {
  // Find the "active" stage (if any) and determine its start
  // timestamp for the live timer. An active stage is either
  // `in_progress` (worker actively running) OR `queued` (the
  // 2026-05-01 Bug 7 fix — bundle past dedup but not yet picked
  // up). Both participate in the "which pill is currently the
  // head of the pipeline?" question; only `in_progress`
  // participates in the live-ticking timer (Bug 10 — a queued
  // pill shows a STATIC elapsed label derived from the
  // `dedup_decided_at` predecessor, not a 1 Hz ticker that
  // falsely suggests work is happening).
  const { activeIndex, activeSince, isQueued } = useMemo(() => {
    const inProgressIdx = stages.findIndex((s) => s.status === "in_progress");
    const queuedIdx = stages.findIndex((s) => s.status === "queued");
    // Migration 026 (2026-05-12) — `gated` and `dismissed` stages
    // are also "active" in the head-of-pipeline sense: they take
    // aria-current and the visual styling slot. They DO NOT take
    // the live-ticking timer (gated bundles aren't elapsing toward
    // anything; dismissed bundles are terminal) so `isQueued`
    // stays false for both.
    //
    // TRIAG-62 (2026-05-21) note: `isQueued` here is the DERIVED
    // "is the active pill a `queued`-status pill?" boolean (true
    // only when `inProgressIdx === -1 && queuedIdx !== -1`). It
    // is NOT the same as `stage.status === "queued"`. The downstream
    // `isInProgressPill` / `useLiveElapsed` / `expectedRemainingMs`
    // predicates check `stage.status === "in_progress"` directly
    // — see the call sites for the reasoning.
    const gatedIdx = stages.findIndex((s) => s.status === "gated");
    const dismissedIdx = stages.findIndex((s) => s.status === "dismissed");
    const idx =
      inProgressIdx !== -1
        ? inProgressIdx
        : queuedIdx !== -1
          ? queuedIdx
          : gatedIdx !== -1
            ? gatedIdx
            : dismissedIdx;
    if (idx === -1) {
      return {
        activeIndex: -1,
        activeSince: null as string | null,
        isQueued: false,
      };
    }
    // Walk backwards from the active stage looking for the
    // most recent done stage to use as the timer origin.
    let since: string | null = null;
    for (let i = idx - 1; i >= 0; i--) {
      const candidate = stages[i];
      if (candidate.status === "done" && candidate.ts) {
        since = candidate.ts;
        break;
      }
    }
    if (!since && pipelineStartedAt) since = pipelineStartedAt;
    return {
      activeIndex: idx,
      activeSince: since,
      // Gated / dismissed never get the queued-elapsed timer;
      // they render static labels via `computeStageLabel`.
      isQueued: inProgressIdx === -1 && queuedIdx !== -1,
    };
  }, [stages, pipelineStartedAt]);

  // Live ticker runs ONLY when the active stage is truly
  // in_progress AND the consuming page marked the bundle as
  // actively analyzing (Bug 10). For a queued stage we render
  // a static "queued Xm" label computed once at render time
  // (no 1 Hz re-render — operators don't benefit from seeing
  // seconds tick up on an analysis that hasn't started yet,
  // and the tick costs a setInterval wake per open bundle
  // page).
  //
  // TRIAG-62 (2026-05-21) fix: the predicate now checks
  // `stages[activeIndex]?.status === "in_progress"` directly
  // instead of `!isQueued`. The pre-fix `!isQueued` check
  // returned `true` for `gated` and `dismissed` head-of-pipeline
  // stages (because the derived `isQueued` boolean is only true
  // when the active pill is a `queued`-status pill), which made
  // the live counter tick on bundles whose analysis never
  // started by design. Surfaced live by Prakash on bundle
  // bfab3c0f sitting at "Analyzing 17h 58m" while the banner
  // directly below said "Auto-analysis off — awaiting operator
  // action." 718 bundles in 30 days were affected.
  const isActiveInProgress =
    activeIndex !== -1 && stages[activeIndex]?.status === "in_progress";
  const liveSeconds = useLiveElapsed(
    activeSince,
    isActiveInProgress && isActivelyAnalyzing,
  );

  // Static "queued Xm" seconds for the queued branch. Computed
  // once per render from the origin timestamp; no re-render on
  // tick. Approximation is fine — an operator hovering the
  // page for a few minutes sees a slightly stale label, which
  // is a correct representation of "this bundle hasn't moved
  // in a few minutes."
  //
  // TRIAG-46 note: the `Date.now()` call below trips
  // `react-hooks/purity` because the value is unstable across
  // re-renders. The intentional staleness is documented above
  // — we deliberately do NOT drive a ticker here, because the
  // "queued" pill is a low-information indicator where exact
  // seconds don't matter and a re-render every second would be
  // wasted work. Per-line disable + this comment is the
  // minimum-surface fix.
  const queuedSeconds = useMemo(() => {
    if (!isQueued || !activeSince) return null;
    const then = Date.parse(activeSince);
    if (Number.isNaN(then)) return null;
    // eslint-disable-next-line react-hooks/purity -- TRIAG-46: intentional staleness; see block comment above the memo.
    return Math.max(0, Math.floor((Date.now() - then) / 1000));
  }, [isQueued, activeSince]);

  // Backward-compat alias — a lot of the pre-Bug-7 code below
  // reads `inProgressIndex`. Keep the name wired to the same
  // value so the rest of the render path doesn't churn; the
  // queued-branch divergence is handled above.
  const inProgressIndex = activeIndex;

  // 2026-05-01 P2b Item 5: compute the "Expected remaining ~Nm"
  // ETA for the analysis_started stage using the per-phase
  // medians endpoint. Memoized on the narrow set of inputs so a
  // 1-Hz live timer tick doesn't re-run the reduce every second.
  //
  // Bug 10 fix (same session): the ETA now ALSO requires
  // `isActivelyAnalyzing === true`. Previously the only gate
  // was `inProgressStage.name === "analysis_started"` which
  // fires on queued bundles too (queued has analysis_started
  // as its `in_progress`-position pill). The
  // sample_count < 3 guard inside `computeExpectedRemainingMs`
  // was the accidental-correctness net on staging's single
  // completed bundle; in production with 3+ completions it
  // would show "≈ 8m remaining" on bundles where the LLM
  // hasn't started, which is actively misleading.
  const expectedRemainingMs = useMemo(() => {
    if (!isActivelyAnalyzing) return null;
    if (isQueued) return null;
    if (inProgressIndex === -1) return null;
    const inProgressStage = stages[inProgressIndex];
    // TRIAG-62 (2026-05-21) guard: only the actually-in-progress
    // analysis_started pill gets the ETA chip. The pre-fix code
    // gated only on `name === "analysis_started"` which fires for
    // gated and dismissed stages too (the stage NAME stays
    // analysis_started; only the STATUS changes to "gated" /
    // "dismissed" / "queued"). Without this guard, an
    // intake_policy="manual" bundle with thick phase medians would
    // render "≈ 8m remaining" alongside the misleading live timer.
    if (inProgressStage.status !== "in_progress") return null;
    // Only the "Analyzing" (analysis_started) pill gets the ETA
    // — every other stage's duration is network / DB latency,
    // not LLM turn time, and the medians capture turn time only.
    if (inProgressStage.name !== "analysis_started") return null;
    return computeExpectedRemainingMs(phaseMedians, completedPhaseLabels);
  }, [
    isActivelyAnalyzing,
    isQueued,
    inProgressIndex,
    stages,
    phaseMedians,
    completedPhaseLabels,
  ]);

  // The pills wrap at every width (Galley #11793). The page column
  // gives the strip ~940 px at any desktop width and 9 pills need more,
  // so the old one-row form from `xl` up (`flex-nowrap` + chevrons)
  // hid "Completed" and "Patches + notify" behind a sideways scroll.
  // The `overflow-x-auto` scroller stays as the belt-and-braces for a
  // single pill wider than a narrow window.
  return (
    <div className="overflow-x-auto">
      <ol
        className={cn("flex flex-wrap items-stretch gap-2", className)}
        aria-label="Pipeline progress"
      >
        {stages.map((stage, i) => {
          const isActivePill = i === inProgressIndex;
          // TRIAG-62 (2026-05-21) fix: previously
          // `isInProgressPill = isActivePill && !isQueued`. The `!isQueued`
          // check returned true for `gated` and `dismissed` stages because
          // `isQueued` is only true when the active pill is a `queued`-status
          // pill (per the useMemo derivation above). The bug was that
          // gated/dismissed bundles got `isInProgressPill = true`, which
          // (a) animate-pulsed the glyph, (b) short-circuited
          // `computeStageLabel`'s `if (isInProgress)` arm before the
          // `case "gated":` / `case "dismissed":` arms could fire —
          // making those switch arms dead code. Tightening to
          // `stage.status === "in_progress"` makes the predicate
          // positively express the intent and unblocks the gated/dismissed
          // labels.
          const isInProgressPill =
            isActivePill && stage.status === "in_progress";
          const isQueuedPill = isActivePill && isQueued;
          const prev = i > 0 ? stages[i - 1] : null;

          // What label renders under the stage name? Rules:
          //
          //   done     → "+delta" from previous done stage, OR
          //              absolute clock time if this is the first
          //              done stage
          //   in_prog  → live "Xs" / "Xm Ys" counter
          //   queued   → static "queued Xm" (no tick — Bug 7/10)
          //   pending  → empty string (no label)
          //   failed   → absolute clock time if `ts` is set, else ""
          //   skipped  → italic "skipped" marker
          const label = computeStageLabel(
            stage,
            prev,
            isInProgressPill,
            liveSeconds,
            isQueuedPill,
            queuedSeconds,
          );

          return (
            <li
              key={stage.name}
              className="flex items-center"
              // aria-current marks the active stage (in_progress
              // OR queued) so screen readers can jump directly to
              // the "head of the pipeline" regardless of whether
              // a worker has picked the bundle up yet.
              aria-current={isActivePill ? "step" : undefined}
            >
              <StagePill
                stage={stage}
                label={label}
                live={isInProgressPill}
                etaLabel={
                  isInProgressPill && expectedRemainingMs !== null
                    ? `≈ ${formatDuration(expectedRemainingMs / 1000)} remaining`
                    : null
                }
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Compute "expected remaining milliseconds" from per-phase
 * medians + the phase_labels already observed on the current
 * analysis. The sum over non-observed medians is the remaining
 * estimate — crudely assumes the current analysis follows the
 * median trajectory, which is good enough for a first-order ETA.
 *
 * Returns `null` (and the UI suppresses the chip) when:
 *   - `phaseMedians` is null/empty — backend hasn't populated
 *     any rows yet (fresh deploy, DB wipe, etc.)
 *   - `completedPhaseLabels` is undefined — caller didn't pass
 *     the guard, so we can't tell remaining from total
 *   - every median's `sample_count < 3` — too thin a sample to
 *     be trustworthy; better to show nothing than a bogus number
 *   - no phase labels remain unobserved — bundle is past every
 *     known phase; ETA is meaningless
 *
 * Exported for unit testing; the production code path calls it
 * through the `expectedRemainingMs` memo above.
 */
// TRIAG-46: pure ETA helper exported for unit testing; HMR degradation
// acceptable.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export function computeExpectedRemainingMs(
  phaseMedians: PhaseMedianLike[] | null | undefined,
  completedPhaseLabels: string[] | undefined,
): number | null {
  if (!phaseMedians || phaseMedians.length === 0) return null;
  if (!completedPhaseLabels) return null;

  // Sample-thickness guard per CONTRIBUTING.md §"Failure
  // visibility" — a confident-looking number derived from two
  // datapoints is worse than no number. Require ≥3 samples on
  // every median feeding the estimate; if any dips below, drop
  // the estimate entirely rather than partially.
  const thinSample = phaseMedians.some((m) => m.sample_count < 3);
  if (thinSample) return null;

  const observed = new Set(completedPhaseLabels);
  let totalMs = 0;
  let remainingCount = 0;
  for (const m of phaseMedians) {
    if (observed.has(m.phase_label)) continue;
    totalMs += m.median_duration_ms;
    remainingCount += 1;
  }
  if (remainingCount === 0) return null;
  return totalMs;
}

// ─── Stage pill ─────────────────────────────────────────────────────────────

interface StagePillProps {
  stage: StageData;
  label: string;
  /**
   * True iff this pill's status is `in_progress` — animates the
   * glyph via `animate-pulse`. Queued pills deliberately do NOT
   * pulse (Bug 7 — a pulsing amber pill implied active work on
   * a queued bundle that was actually untouched).
   */
  live: boolean;
  /**
   * Optional second line rendered under the elapsed label — the
   * "≈ 8m remaining" ETA from `computeExpectedRemainingMs`. Only
   * ever populated on the in-progress analysis_started pill; the
   * rendering code suppresses the line entirely when null.
   */
  etaLabel?: string | null;
}

function StagePill({ stage, label, live, etaLabel }: StagePillProps) {
  const statusCls = STATUS_STYLES[stage.status];
  const glyph = STATUS_GLYPHS[stage.status];
  const aria = STATUS_ARIA[stage.status];

  return (
    <div
      className={cn(
        "flex min-w-[6.5rem] flex-col items-center gap-0.5 rounded-lg border px-3 py-2 text-sm shadow-sm",
        statusCls,
      )}
      aria-label={`${stageLabel(stage.name)}: ${aria}`}
      title={
        stage.ts
          ? `${stageLabel(stage.name)} — ${aria} at ${formatClock(stage.ts)} ${zoneAbbrev(new Date(stage.ts))}`
          : `${stageLabel(stage.name)} — ${aria}`
      }
    >
      <div className="flex items-center gap-1.5 font-medium">
        <span
          aria-hidden="true"
          className={cn("inline-block", live && "animate-pulse")}
        >
          {glyph}
        </span>
        <span>{stageLabel(stage.name)}</span>
      </div>
      {/* Secondary label (e.g. dedup methodology). Rendered below
          the name in a muted italic. Hidden when absent so the
          pill doesn't reserve vertical space for a blank line. */}
      {stage.subLabel && (
        <div className="text-xs italic opacity-80">{stage.subLabel}</div>
      )}
      {/* Time / delta / live-counter label. `aria-live="polite"`
          on the in-progress case so a screen reader announces the
          elapsed time periodically — but not on every 1s tick
          (the browser's aria-live throttling handles that), and
          not on static labels (which don't change). */}
      <div
        className={cn(
          "font-mono text-[0.7rem] opacity-90",
          label.length === 0 && "invisible",
        )}
        aria-live={live ? "polite" : undefined}
      >
        {label || "—"}
      </div>
      {/* 2026-05-01 P2b Item 5: ETA chip under the in-progress
          pill. Suppressed (not just empty) when absent so the
          pill's layout doesn't reserve blank space on stages
          without an ETA. Italic + slightly muted — signals "this
          is an estimate, not ground truth" without a separate
          tooltip. */}
      {etaLabel && (
        <div
          className="text-[0.7rem] italic opacity-75"
          aria-label={`estimated ${etaLabel}`}
        >
          {etaLabel}
        </div>
      )}
    </div>
  );
}

// ─── Label computation ──────────────────────────────────────────────────────

/**
 * Produce the time / delta / live-counter label for a stage pill
 * given the stage itself, its predecessor, whether it's the
 * in-progress stage, and (if so) the current live seconds count.
 *
 * Extracted as a pure function so the rendering logic stays
 * declarative and the "which label goes where" rules have a
 * single readable home.
 */
function computeStageLabel(
  stage: StageData,
  prev: StageData | null,
  isInProgress: boolean,
  liveSeconds: number | null,
  isQueued: boolean = false,
  queuedSeconds: number | null = null,
): string {
  if (isInProgress) {
    // In-progress stage shows the live counter. `null` liveSeconds
    // means we have no origin timestamp to tick from — render a
    // static "…" rather than a bogus 0s.
    return liveSeconds === null ? "…" : formatDuration(liveSeconds);
  }

  // 2026-05-01 Bug 7/10 fix: queued stage — the bundle has
  // reached the head of the pipeline (predecessor done) but no
  // worker has picked it up yet. Render a STATIC "queued Xm"
  // label from the `dedup_decided_at` predecessor timestamp, no
  // 1 Hz live ticker (that would falsely suggest work is
  // happening on an untouched bundle). `null` queuedSeconds →
  // render just "queued" as a safe fallback when we have no
  // predecessor timestamp to time from.
  if (isQueued) {
    return queuedSeconds === null
      ? "queued"
      : `queued ${formatDuration(queuedSeconds)}`;
  }

  switch (stage.status) {
    case "done": {
      if (!stage.ts) return "";
      // Show +delta from the most recent done predecessor. If
      // there isn't one (this is the first done stage), show the
      // absolute clock time instead.
      if (prev && prev.status === "done" && prev.ts) {
        return formatDelta(prev.ts, stage.ts);
      }
      return formatClock(stage.ts);
    }
    case "failed":
      return stage.ts ? formatClock(stage.ts) : "";
    case "skipped":
      return "skipped";
    case "queued":
      // The `isQueued` branch above already returns for the
      // currently-active queued pill. This arm handles the
      // edge case of a `queued`-status stage that isn't the
      // first queued pill (shouldn't happen at the current
      // `deriveStages` design — only `analysis_started` ever
      // becomes `queued` — but the switch stays exhaustive so
      // a future derivation change doesn't silently fall
      // through to a blank label).
      return "queued";
    case "gated":
      // Migration 026 (2026-05-12) — gated bundles render a
      // static "awaiting operator" label. No timer because
      // "how long this has been gated" isn't operationally
      // meaningful in the way "how long a worker has been
      // analyzing" is. The banner above the strip carries the
      // detailed copy.
      return "awaiting operator";
    case "dismissed":
      // Migration 026 (2026-05-12) — dismissed bundles render
      // a terminal label. The DismissedSection banner below
      // the strip carries who/when/why.
      return "dismissed";
    case "pending":
    case "in_progress":
      // `in_progress` is handled above; including it here keeps
      // the switch exhaustive for TypeScript's check without a
      // `default` arm (which would hide a future new status).
      return "";
  }
}
